
import React from 'react';
import { Card } from "@/components/ui/card";
import { Match } from '../../types/head-to-head';
import { ProgressBar } from '@/components/league-table/stats/components/ProgressBar';

interface FoulsSummaryCardProps {
  matches: Match[];
}

export const FoulsSummaryCard: React.FC<FoulsSummaryCardProps> = ({ matches }) => {
  const calculateFoulsStats = () => {
    const thresholds = [5.5, 6.5, 7.5, 8.5, 9.5, 10.5, 11.5, 12.5, 13.5];
    const stats = thresholds.map(threshold => {
      const homeOver = matches.filter(match => {
        const fouls = parseInt(match.HF) || 0;
        return fouls > threshold;
      }).length;

      const awayOver = matches.filter(match => {
        const fouls = parseInt(match.AF) || 0;
        return fouls > threshold;
      }).length;

      return {
        label: `>${threshold}`,
        homeValue: homeOver,
        awayValue: awayOver,
        total: matches.length || 1 // Prevent division by zero
      };
    });

    return stats;
  };

  const calculateAverages = () => {
    const totalHomeFouls = matches.reduce((sum, match) => sum + (parseInt(match.HF) || 0), 0);
    const totalAwayFouls = matches.reduce((sum, match) => sum + (parseInt(match.AF) || 0), 0);
    const matchCount = matches.length || 1; // Prevent division by zero
    
    const homeAvg = totalHomeFouls / matchCount;
    const awayAvg = totalAwayFouls / matchCount;
    
    return {
      home: isNaN(homeAvg) ? '0' : homeAvg.toFixed(1),
      away: isNaN(awayAvg) ? '0' : awayAvg.toFixed(1)
    };
  };

  const stats = calculateFoulsStats();
  const averages = calculateAverages();

  return (
    <Card className="p-4">
      <h3 className="text-lg font-semibold mb-4">Team Fouls</h3>
      <div className="grid grid-cols-11 items-center gap-2 mb-6">
        <div className="col-span-2 text-right text-blue-600 text-sm font-medium">
          Avg: {averages.home}
        </div>
        <div className="col-span-3">
        </div>
        <div className="col-span-1 text-center text-sm font-medium">
          Average
        </div>
        <div className="col-span-3">
        </div>
        <div className="col-span-2 text-left text-red-600 text-sm font-medium">
          Avg: {averages.away}
        </div>
      </div>
      <div className="space-y-4">
        {stats.map(({ label, homeValue, awayValue, total }) => {
          const homePercentage = total > 0 ? ((homeValue / total) * 100) : 0;
          const awayPercentage = total > 0 ? ((awayValue / total) * 100) : 0;
          const formattedHomePercentage = isNaN(homePercentage) ? '0' : homePercentage.toFixed(1);
          const formattedAwayPercentage = isNaN(awayPercentage) ? '0' : awayPercentage.toFixed(1);
          
          return (
            <div key={label} className="space-y-2">
              <div className="grid grid-cols-11 items-center gap-2">
                <div className="col-span-2 text-right text-blue-600 text-sm whitespace-nowrap">
                  {homeValue || 0} ({formattedHomePercentage}%)
                </div>
                <div className="col-span-3 relative">
                  <ProgressBar percentage={formattedHomePercentage} color="bg-blue-500" direction="left" />
                </div>
                <div className="col-span-1 text-center text-sm">{label}</div>
                <div className="col-span-3 relative">
                  <ProgressBar percentage={formattedAwayPercentage} color="bg-red-500" direction="right" />
                </div>
                <div className="col-span-2 text-left text-red-600 text-sm whitespace-nowrap">
                  {awayValue || 0} ({formattedAwayPercentage}%)
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
};
